import { FlowStepData } from "../FlowStepData";
import { MultiExecutableOutputDefinition } from "../../../Common/Execution/MultiExecutable";
import { ValidationIssue } from "../../../Common/IDesignValidationProvider";
import { ExpressionConfiguration } from "../../../Expressions/ExpressionConfiguration";
import { DateDataDefinition } from "../../../Common/Data/Definitions/DateDataDefinition";
import { FlowUtilities } from "../../Utilities/FlowUtilities";
import { StringDataDefinition } from "../../../Common/Data/Definitions/StringDataDefinition";
import { ExpressionValueType } from "../../../Expressions/ExpressionValueType";
import { DateAddXExpression } from "../../../Expressions/Expressions/Date/DateAddXExpression";
import { DateNowExpression } from "../../../Expressions/Expressions/Date/DateNowExpression";
import { DateSpanType } from "../../../Expressions/Expressions/Date/BaseDateCalculateExpression";
import { FlowConfiguration } from "../../FlowConfiguration";
import { DataDefinition } from "../../../Common/Data/DataDefinition";

export enum FormAssignmentType {
    CURRENT_USER = "Current User",
    ASSIGNED = "Assigned",
    EXTERNAL = "External",
}

export class FormStep extends FlowStepData {
    static readonly ASSIGNMENT_ID = "assignment_id";
    static readonly ASSIGNMENT_LINK = "assignment_link";
    static readonly EXTERNAL_CHANNEL_NAME = "External";
    readonly __type: string = "run_form";
    embeddedForm: boolean = false;
    externalFormId?: string;

    formData: object | undefined = undefined;
    outcomePaths: MultiExecutableOutputDefinition[] = [];

    // to store
    assignForm: FormAssignmentType = FormAssignmentType.CURRENT_USER;
    channelName: ExpressionConfiguration = new ExpressionConfiguration();
    assignToChannelMember: boolean = false;
    channelMemberAssignment: ExpressionConfiguration = new ExpressionConfiguration();

    expireForm: boolean = false;
    cancelFlowOnExpire: boolean = false;
    expireFormTime: ExpressionConfiguration = new ExpressionConfiguration();

    taskName: ExpressionConfiguration = new ExpressionConfiguration();
    taskDescription: ExpressionConfiguration = new ExpressionConfiguration();
    dueDate: ExpressionConfiguration = new ExpressionConfiguration();
    warnDate: ExpressionConfiguration = new ExpressionConfiguration();
    actionName: string = "Open";

    startFlow: FlowConfiguration = new FlowConfiguration();
    endFlow: FlowConfiguration = new FlowConfiguration();

    constructor() {
        super();
        this.name = "Form";
        this.onDataChange = () => {
            this.setupExpressions();
        };
        this.setupExpressions();
    }

    getOutcomes(): MultiExecutableOutputDefinition[] {
        return this.outcomePaths;
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "assignForm", this.assignForm);
        Reflect.set(data, "actionName", this.actionName);
        Reflect.set(data, "channelName", this.channelName.getStorageData());
        Reflect.set(data, "assignToChannelMember", this.assignToChannelMember);
        Reflect.set(data, "channelMemberAssignment", this.channelMemberAssignment.getStorageData());

        Reflect.set(data, "expireForm", this.expireForm);
        Reflect.set(data, "cancelFlowOnExpire", this.cancelFlowOnExpire);
        Reflect.set(data, "expireFormTime", this.expireFormTime.getStorageData());

        Reflect.set(data, "taskName", this.taskName.getStorageData());
        Reflect.set(data, "taskDescription", this.taskDescription.getStorageData());
        Reflect.set(data, "dueDate", this.dueDate.getStorageData());
        Reflect.set(data, "warnDate", this.warnDate.getStorageData());
        Reflect.set(data, "startFlow", this.startFlow.getStorageData());
        Reflect.set(data, "endFlow", this.endFlow.getStorageData());

        Reflect.set(data, "formData", this.formData);

        Reflect.set(data, "embeddedForm", this.embeddedForm);
        Reflect.set(data, "externalFormId", this.externalFormId);

        let paths: object[] = [];

        for (let p of this.outcomePaths) {
            paths.push(p.getStorageData());
        }

        Reflect.set(data, "outcomePaths", paths);
        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        if (!data || Object.values(data).length === 0) return;

        this.assignForm = Reflect.get(data, "assignForm");
        this.actionName = Reflect.get(data, "actionName");
        this.channelName.setStorageData(Reflect.get(data, "channelName"));
        this.assignToChannelMember = Reflect.get(data, "assignToChannelMember");
        this.channelMemberAssignment.setStorageData(Reflect.get(data, "channelMemberAssignment"));

        this.expireForm = Reflect.get(data, "expireForm");
        this.cancelFlowOnExpire = Reflect.get(data, "cancelFlowOnExpire");
        this.expireFormTime.setStorageData(Reflect.get(data, "expireFormTime"));

        this.taskName.setStorageData(Reflect.get(data, "taskName"));
        this.taskDescription.setStorageData(Reflect.get(data, "taskDescription"));
        this.dueDate.setStorageData(Reflect.get(data, "dueDate"));
        this.warnDate.setStorageData(Reflect.get(data, "warnDate"));

        this.endFlow.setStorageData(Reflect.get(data, "endFlow"));
        this.startFlow.setStorageData(Reflect.get(data, "startFlow"));

        this.formData = Reflect.get(data, "formData");

        this.embeddedForm = Reflect.get(data, "embeddedForm");
        this.externalFormId = Reflect.get(data, "externalFormId");

        let outcomes: MultiExecutableOutputDefinition[] = [];

        for (let o of Reflect.get(data, "outcomePaths")) {
            let outcome = new MultiExecutableOutputDefinition();

            outcome.setStorageData(o);

            outcomes.push(outcome);
        }

        this.outcomePaths = outcomes;

        // call this to set input data, etc
        this.setupExpressions();
    }

    override getValidationIssues(): ValidationIssue[] {
        let issues = super.getValidationIssues();

        if (this.formData === undefined) issues.push(new ValidationIssue("Form data not configured"));

        if (this.outcomePaths.length === 0) issues.push(new ValidationIssue("At least one outcome path is required"));

        return issues;
    }

    private setupExpressions() {
        this.dueDate.allowRootUnset = true;
        this.taskName.executionResult = new StringDataDefinition();
        this.taskDescription.executionResult = new StringDataDefinition();

        this.channelName.executionResult = new StringDataDefinition();
        this.channelMemberAssignment.executionResult = new StringDataDefinition();
        this.channelMemberAssignment.executionResult.list = true;

        this.expireFormTime.executionResult = new DateDataDefinition();

        if (this.expireFormTime.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET) {
            let exp = new DateAddXExpression();
            exp.baseDate.subExpression = new DateNowExpression();
            exp.calculateType = DateSpanType.MINUTES;
            exp.adjustBy.value = 30;
            this.expireFormTime.rootExpression.expressionRoot.subExpression = exp;
        }

        this.dueDate.executionResult = new DateDataDefinition();
        this.warnDate.executionResult = new DateDataDefinition();
        if (this.sequence?.flow) {
            let data = FlowUtilities.getDataAtStep(this.sequence?.flow, this, true);
            let f = () => {
                return data;
            };
            this.taskName.inputData = f;
            this.taskDescription.inputData = f;
            this.channelName.inputData = f;
            this.channelMemberAssignment.inputData = f;
            this.expireFormTime.inputData = f;
            this.dueDate.inputData = f;
            this.warnDate.inputData = f;

            let flowData = new DataDefinition();

            flowData.fields = [
                ...data.fields,
                new StringDataDefinition(FormStep.ASSIGNMENT_ID),
                new StringDataDefinition(FormStep.ASSIGNMENT_LINK),
            ];

            let f2 = () => {
                return flowData;
            };

            this.endFlow.inputData = f2;
            this.startFlow.inputData = f2;
        }
    }
}
